import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Loader } from '../../../components/ui';
import { cn } from '../../../lib/utils';

interface UploadStatusProps {
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  isUploading: boolean;
  onClose: () => void;
}

export function UploadStatus({
  totalFiles,
  completedFiles,
  failedFiles,
  isUploading,
  onClose,
}: UploadStatusProps) {
  const progress = Math.round((completedFiles / totalFiles) * 100);
  const allCompleted = completedFiles + failedFiles === totalFiles;

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80',
        'transform transition-transform duration-300',
        isUploading || !allCompleted ? 'translate-y-0' : 'translate-y-[150%]'
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-gray-900">
          {allCompleted ? 'Upload Complete' : 'Uploading Images...'}
        </h3>
        {allCompleted && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle size={20} />
          </button>
        )}
      </div>

      {/* Progress bar */}
      {!allCompleted && (
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Status */}
      <div className="flex items-center gap-2 text-sm">
        {!allCompleted ? (
          <>
            <Loader size="small" color="text-blue-500" />
            <span className="text-gray-600">
              Uploading {completedFiles} of {totalFiles} images
            </span>
          </>
        ) : failedFiles > 0 ? (
          <>
            <XCircle size={16} className="text-red-500" />
            <span className="text-gray-600">
              {completedFiles} uploaded, {failedFiles} failed
            </span>
          </>
        ) : (
          <>
            <CheckCircle2 size={16} className="text-green-500" />
            <span className="text-gray-600">Successfully uploaded {completedFiles} images</span>
          </>
        )}
      </div>
    </div>
  );
}
